import { Usuario } from '@/types';
import { UsuariosService } from '@/services';

let usuariosCache: Map<number, Usuario> = new Map();
let usuariosCachePromise: Promise<void> | null = null;

/**
 * Carrega todos os usuários e armazena em cache
 */
async function loadUsersCache(): Promise<void> {
  if (usuariosCachePromise) {
    return usuariosCachePromise;
  }

  usuariosCachePromise = (async () => {
    try {
      const usuarios = await UsuariosService.getUsuarios();
      usuariosCache.clear();
      usuarios.forEach(user => {
        usuariosCache.set(user.id, user);
      });
    } catch (error) {
      console.error('Erro ao carregar usuários para cache de menções:', error);
    }
  })();

  return usuariosCachePromise;
}

/**
 * Converte HTML do Quill (com spans de menção) para formato @[id:14] antes de salvar
 * Procura por spans com data-user-id e converte para @[id:14]
 */
export function convertQuillMentionsToBackendFormat(html: string): string {
  if (!html) return html;

  let processedHtml = html;

  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = processedHtml;
    const mentionSpans = tempDiv.querySelectorAll('span[data-user-id]');
    
    mentionSpans.forEach((span) => {
      const userId = span.getAttribute('data-user-id');
      if (userId) {
        const mentionText = `@[id:${userId}]`;
        const textNode = document.createTextNode(mentionText);
        span.parentNode?.replaceChild(textNode, span);
      }
    });
    
    processedHtml = tempDiv.innerHTML;
  } catch (error) {
    console.warn('Erro ao processar menções com querySelector:', error);
  }

  // Regex como fallback para capturar spans que possam ter sido perdidos
  // Procura por: <span ... data-user-id="123" ...>@Nome</span>
  const mentionSpanRegex = /<span[^>]*data-user-id="(\d+)"[^>]*>@[^<]*<\/span>/gi;
  processedHtml = processedHtml.replace(mentionSpanRegex, (match, userId) => {
    return `@[id:${userId}]`;
  });

  // Se não encontramos spans com data-user-id, pode ser que o Quill tenha convertido para texto
  // Nesse caso, vamos procurar por padrões de texto "@Nome" e tentar identificar o usuário
  // Mas isso é menos confiável, então vamos apenas retornar o HTML processado

  return processedHtml;
}

/**
 * Converte formato @[id:14] para spans HTML com nome do usuário para exibir no Quill
 * Esta função deve ser chamada antes de definir o conteúdo no Quill
 */
export async function convertBackendMentionsToQuillFormat(html: string): Promise<string> {
  if (!html) return html;

  // Carregar cache de usuários
  await loadUsersCache();

  // Regex para encontrar menções no formato @[id:123]
  const mentionRegex = /@\[id:(\d+)\]/g;
  
  let processedHtml = html;
  const mentions = Array.from(html.matchAll(mentionRegex));
  
  // Processar cada menção encontrada
  for (const mention of mentions) {
    const fullMatch = mention[0]; // @[id:123]
    const userId = parseInt(mention[1]); // 123
    
    const user = usuariosCache.get(userId);
    const userName = user?.nome || `Usuário #${userId}`;
    
    // Criar span HTML para o Quill (sem estilos complexos, apenas o necessário)
    const mentionHtml = `<span class="mention-inline" data-user-id="${userId}" contenteditable="false" style="display: inline-block; padding: 2px 8px; margin: 0 2px; background: #10B981; color: white; border-radius: 12px; font-size: 0.875rem; font-weight: 500; cursor: default;">@${userName}</span>`;
    
    // Substituir a menção pelo HTML
    processedHtml = processedHtml.replace(fullMatch, mentionHtml);
  }
  
  return processedHtml;
}

/**
 * Insere uma menção no Quill com o nome do usuário
 * Retorna o HTML do span que deve ser inserido
 */
export function createMentionHTML(userId: number, userName: string): string {
  return `<span class="mention-inline" data-user-id="${userId}" contenteditable="false" style="display: inline-block; padding: 2px 8px; margin: 0 2px; background: #10B981; color: white; border-radius: 12px; font-size: 0.875rem; font-weight: 500; cursor: default;">@${userName}</span>`;
}

/**
 * Limpa o cache de usuários
 */
export function clearUsersCache(): void {
  usuariosCache.clear();
  usuariosCachePromise = null;
}

